import { ComponentChildren } from 'preact';

import { classNames } from '#utils/UNSAFE_classNames';
import { useFormContext } from '#hooks/UNSAFE_useFormContext';
import { useFormFieldContext } from '#hooks/UNSAFE_useFormFieldContext';
import { Size } from '#utils/UNSAFE_size';
import { useComponentTheme } from '#hooks/UNSAFE_useComponentTheme';
import { LabelRedwoodTheme } from '#UNSAFE_Label/themes/redwood/LabelTheme';
import { FormLayoutRedwoodTheme } from '#UNSAFE_TextField/themes/redwood/FormLayoutTheme';
import {
  labelSlotStyles,
  labelInnerStyles
} from '#UNSAFE_LabelValueLayout/LabelValueLayoutStyles.css';
import { LayoutColumnSpan } from '#utils/UNSAFE_styles/Layout';
import { computeStartLabelWidth } from '#utils/UNSAFE_formControlUtils';
import { StyledLabelValueLayout } from '#UNSAFE_LabelValueLayout/StyledLabelValueLayout';
import { switchLabelInnerStyles } from './SwitchLabelValueLayoutStyles.css';

/*
 * SwitchLabelValueLayout - direct JSX/classNames/spacing copy from CheckboxRadioLabelValueLayout,
 * with all radio/checkbox conditionals removed.
 */
type Props = {
  /**
   * Specifies how many columns to span in a FormLayout with direction === 'row'
   */
  columnSpan?: LayoutColumnSpan;
  /**
   * Defines how the label is going to be positioned. For Switch, the 'start', 'top', and 'inside' values are supported.
   */
  labelEdge?: 'start' | 'top' | 'inside';
  /**
   * Label content of the layout
   */
  label?: ComponentChildren;
  /**
   * Defines the label width for labelEdge 'start' ('top' is always 100%)
   */
  labelStartWidth?: Size;
  /**
   * Value content of the layout
   */
  children: ComponentChildren;
};

export const SwitchLabelValueLayout = ({
  columnSpan = 1,
  label,
  labelEdge = 'start',
  children,
  labelStartWidth = '33%'
}: Props) => {
  const { baseTheme: labelTheme } = useComponentTheme(LabelRedwoodTheme);
  const { baseTheme: formLayoutTheme } = useComponentTheme(FormLayoutRedwoodTheme);

  const isStart = labelEdge === 'start';
  const isTopOrInside = labelEdge === 'top' || labelEdge === 'inside';

  // labelStartWidth needs to take columnSpan into account
  const width = computeStartLabelWidth(labelStartWidth, columnSpan);
  const valueWidth = `calc(100% - ${width})`;

  const { isFormLayout, isReadonly: isFormReadonly } = useFormContext();
  const { isReadonly } = useFormFieldContext();
  const isPureReadonly = (isFormLayout && isFormReadonly) || (!isFormLayout && isReadonly);

  const labelStyleClasses = classNames([
    formLayoutTheme,
    labelSlotStyles.base,
    isStart && labelSlotStyles.start,
    isTopOrInside && labelSlotStyles.top,
    isTopOrInside && isPureReadonly && labelSlotStyles.topPureReadonly
  ]);
  const labelInnerClasses = classNames([
    labelTheme,
    formLayoutTheme,
    labelInnerStyles.base,
    isStart && labelInnerStyles.start,
    isStart && switchLabelInnerStyles.startSwitch
  ]);
  const labelStyles = isStart ? { flexBasis: width, width, maxWidth: width } : {};
  const valueStyles = isStart
    ? { flexBasis: valueWidth, width: valueWidth, maxWidth: valueWidth }
    : {};

  // Outer container and inner wrapper match CheckboxRadioLabelValueLayout's order exactly:
  return (
    <StyledLabelValueLayout
      flexAlign={isReadonly && isStart ? 'stretch' : 'start'}
      labelStyleClasses={labelStyleClasses}
      labelStyles={labelStyles}
      labelInnerClasses={labelInnerClasses}
      label={label}
      valueStyles={valueStyles}>
      {children}
    </StyledLabelValueLayout>
  );
};
